import {
  encodeRealtimeMessage,
  parseServerMessage,
  REALTIME_SUBPROTOCOL,
  type ClientMessage,
  type ServerMessage,
  type TrackRecordDto,
} from '@cars/shared';
import { getSessionToken } from '../api/client';

export type RealtimeStatus = 'offline' | 'connecting' | 'live' | 'stale';
export type RealtimeEvent =
  | { type: 'status'; status: RealtimeStatus }
  | { type: 'snapshot'; records: TrackRecordDto[] }
  | { type: 'record-updated'; record: TrackRecordDto }
  | { type: 'error'; code: string; message: string };

export interface RealtimeClientOptions {
  tokenProvider?: () => string | null;
  websocketFactory?: (url: string, protocols: string) => WebSocket;
  random?: () => number;
  environment?: RealtimeEnvironment;
  locationProvider?: () => Pick<Location, 'protocol' | 'host'>;
  websocketOpenState?: number;
}

export interface RealtimeEnvironment {
  isOnline: () => boolean;
  visibilityState: () => DocumentVisibilityState;
  setTimeout: (handler: () => void, timeoutMs: number) => number;
  clearTimeout: (handle: number) => void;
  addWindowListener: (type: 'online' | 'offline', listener: () => void) => void;
  removeWindowListener: (type: 'online' | 'offline', listener: () => void) => void;
  addDocumentListener: (type: 'visibilitychange', listener: () => void) => void;
  removeDocumentListener: (type: 'visibilitychange', listener: () => void) => void;
}

const RETRY_CAP_MS = 30_000;

/** One application-level, same-origin WebSocket client shared by every scene. */
export class RealtimeClient {
  private readonly tokenProvider: () => string | null;
  private readonly websocketFactory: (url: string, protocols: string) => WebSocket;
  private readonly random: () => number;
  private readonly environment: RealtimeEnvironment;
  private readonly locationProvider: () => Pick<Location, 'protocol' | 'host'>;
  private readonly websocketOpenState: number;
  private readonly listeners = new Set<(event: RealtimeEvent) => void>();
  private readonly subscriptions = new Map<string, Set<number>>();
  private readonly currentRecords = new Map<string, TrackRecordDto>();
  private socket: WebSocket | null = null;
  private retryTimer: number | null = null;
  private stableTimer: number | null = null;
  private retryAttempt = 0;
  private started = false;
  private explicitClose = false;
  private status: RealtimeStatus = 'offline';

  constructor(options: RealtimeClientOptions = {}) {
    this.tokenProvider = options.tokenProvider ?? getSessionToken;
    this.websocketFactory =
      options.websocketFactory ?? ((url, protocols) => new WebSocket(url, protocols));
    this.random = options.random ?? Math.random;
    this.environment = options.environment ?? {
      isOnline: () => navigator.onLine,
      visibilityState: () => document.visibilityState,
      setTimeout: (handler, timeoutMs) => window.setTimeout(handler, timeoutMs),
      clearTimeout: (handle) => window.clearTimeout(handle),
      addWindowListener: (type, listener) => window.addEventListener(type, listener),
      removeWindowListener: (type, listener) => window.removeEventListener(type, listener),
      addDocumentListener: (type, listener) => document.addEventListener(type, listener),
      removeDocumentListener: (type, listener) => document.removeEventListener(type, listener),
    };
    this.locationProvider = options.locationProvider ?? (() => window.location);
    this.websocketOpenState = options.websocketOpenState ?? 1;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.explicitClose = false;
    this.suspended = !this.canConnect();
    this.environment.addWindowListener('online', this.handleOnline);
    this.environment.addWindowListener('offline', this.handleOffline);
    this.environment.addDocumentListener('visibilitychange', this.handleVisibility);
    this.connectIfPossible();
  }

  stop(): void {
    this.explicitClose = true;
    this.started = false;
    this.suspended = true;
    this.clearRetry();
    this.clearStableTimer();
    this.environment.removeWindowListener('online', this.handleOnline);
    this.environment.removeWindowListener('offline', this.handleOffline);
    this.environment.removeDocumentListener('visibilitychange', this.handleVisibility);
    const socket = this.socket;
    this.socket = null;
    socket?.close(1000, 'Client stopped');
    this.setStatus('offline');
  }

  on(listener: (event: RealtimeEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  getCurrentRecord(trackId: number, rulesetId: string): TrackRecordDto | null {
    return this.currentRecords.get(this.recordKey(trackId, rulesetId)) ?? null;
  }

  subscribe(trackIds: readonly number[], rulesetId: string): void {
    const tracks = this.subscriptions.get(rulesetId) ?? new Set<number>();
    for (const trackId of trackIds) tracks.add(trackId);
    this.subscriptions.set(rulesetId, tracks);
    this.send({ v: 1, type: 'subscribe-records', trackIds: [...trackIds], rulesetId });
  }

  unsubscribe(trackIds: readonly number[]): void {
    this.send({ v: 1, type: 'unsubscribe-records', trackIds: [...trackIds] });
    for (const tracks of this.subscriptions.values())
      for (const trackId of trackIds) tracks.delete(trackId);
  }

  authenticate(): void {
    const token = this.tokenProvider();
    if (token) this.send({ v: 1, type: 'auth', token });
  }

  private connectIfPossible(): void {
    if (!this.started || this.explicitClose || !this.canConnect() || this.socket) return;
    const location = this.locationProvider();
    const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.setStatus('connecting');
    try {
      const socket = this.websocketFactory(`${scheme}//${location.host}/ws`, REALTIME_SUBPROTOCOL);
      this.socket = socket;
      socket.addEventListener('open', () => this.handleOpen(socket));
      socket.addEventListener('message', (event) => this.handleMessage(socket, event));
      socket.addEventListener('close', (event) => this.handleClose(socket, event));
      socket.addEventListener('error', () => this.handleError(socket));
    } catch {
      this.socket = null;
      this.scheduleRetry();
    }
  }

  private readonly handleOpen = (socket: WebSocket): void => {
    if (this.socket !== socket || !this.started || this.suspended) return;
    this.clearStableTimer();
    this.setStatus('live');
    this.stableTimer = this.environment.setTimeout(() => {
      if (this.socket !== socket || !this.started || this.suspended) return;
      this.stableTimer = null;
      this.retryAttempt = 0;
    }, 10_000);
  };

  private readonly handleMessage = (socket: WebSocket, event: MessageEvent): void => {
    if (this.socket !== socket) return;
    if (typeof event.data !== 'string') {
      socket.close(1003, 'Binary messages are not supported');
      return;
    }
    let decoded: unknown;
    try {
      decoded = JSON.parse(event.data) as unknown;
    } catch {
      socket.close(1003, 'Invalid JSON');
      return;
    }
    const message = parseServerMessage(decoded);
    if (!message) {
      socket.close(1003, 'Invalid message');
      return;
    }
    this.handleServerMessage(socket, message);
  };

  private handleServerMessage(socket: WebSocket, message: ServerMessage): void {
    if (this.socket !== socket) return;
    if (message.type === 'ready') {
      this.setStatus('live');
      this.authenticate();
      for (const [rulesetId, tracks] of this.subscriptions)
        this.send({ v: 1, type: 'subscribe-records', trackIds: [...tracks], rulesetId });
      return;
    }
    if (message.type === 'error') {
      this.emit({ type: 'error', code: message.code, message: message.message });
      if (message.code === 'auth_invalid' || message.code === 'protocol_unsupported')
        this.explicitClose = true;
      return;
    }
    if (message.type === 'record-snapshot') {
      const records = message.records.filter((record) => this.acceptRevision(record, true));
      this.emit({ type: 'snapshot', records });
      return;
    }
    if (this.acceptRevision(message.record, false))
      this.emit({ type: 'record-updated', record: message.record });
  }

  private acceptRevision(record: TrackRecordDto, allowRepair: boolean): boolean {
    const key = this.recordKey(record.trackId, record.rulesetId);
    const current = this.currentRecords.get(key);
    if (current && record.revision < current.revision) return false;
    if (current && record.revision === current.revision) {
      if (!allowRepair || this.sameRecord(current, record)) return false;
    }
    this.currentRecords.set(key, record);
    return true;
  }

  private readonly handleClose = (socket: WebSocket, event: CloseEvent): void => {
    if (this.socket !== socket) return;
    this.clearStableTimer();
    this.socket = null;
    if (
      event.code === 1002 ||
      event.code === 1003 ||
      event.code === 1008 ||
      this.explicitClose ||
      !this.started ||
      this.suspended ||
      !this.canConnect()
    ) {
      this.explicitClose = event.code === 1002 || event.code === 1003 || event.code === 1008;
      this.setStatus('offline');
      return;
    }
    this.setStatus('stale');
    this.scheduleRetry();
  };

  private readonly handleError = (socket: WebSocket): void => {
    if (this.socket !== socket) return;
    this.clearStableTimer();
    if (this.suspended || !this.canConnect()) {
      this.setStatus('offline');
      return;
    }
    this.setStatus('stale');
  };
  private readonly handleOnline = (): void => {
    if (!this.started || this.environment.visibilityState() === 'hidden') return;
    this.suspended = false;
    this.connectIfPossible();
  };
  private readonly handleOffline = (): void => {
    this.suspended = true;
    this.clearRetry();
    this.clearStableTimer();
    const socket = this.socket;
    this.socket = null;
    socket?.close(1000, 'Client offline');
    this.setStatus('offline');
  };
  private readonly handleVisibility = (): void => {
    if (this.environment.visibilityState() === 'hidden') {
      this.suspended = true;
      this.clearRetry();
      this.clearStableTimer();
      const socket = this.socket;
      this.socket = null;
      socket?.close(1000, 'Client hidden');
      this.setStatus('offline');
      return;
    }
    if (!this.environment.isOnline()) return;
    this.suspended = false;
    if (this.socket?.readyState !== this.websocketOpenState) this.connectIfPossible();
  };

  private scheduleRetry(): void {
    if (
      this.retryTimer !== null ||
      !this.started ||
      this.explicitClose ||
      !this.canConnect() ||
      this.suspended
    )
      return;
    const base = Math.min(RETRY_CAP_MS, 500 * 2 ** Math.min(this.retryAttempt, 6));
    this.retryAttempt += 1;
    const delay = Math.round(base * (0.75 + this.random() * 0.5));
    this.retryTimer = this.environment.setTimeout(() => {
      this.retryTimer = null;
      this.connectIfPossible();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) this.environment.clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private clearStableTimer(): void {
    if (this.stableTimer !== null) this.environment.clearTimeout(this.stableTimer);
    this.stableTimer = null;
  }

  private suspended = false;

  private canConnect(): boolean {
    return this.environment.isOnline() && this.environment.visibilityState() !== 'hidden';
  }

  private recordKey(trackId: number, rulesetId: string): string {
    return `${trackId}:${rulesetId}`;
  }

  private sameRecord(left: TrackRecordDto, right: TrackRecordDto): boolean {
    return (
      left.trackId === right.trackId &&
      left.rulesetId === right.rulesetId &&
      left.revision === right.revision &&
      left.timeMs === right.timeMs &&
      left.sectorTimesMs.every((value, index) => value === right.sectorTimesMs[index]) &&
      left.carId === right.carId &&
      left.playerDisplayName === right.playerDisplayName &&
      left.replayId === right.replayId &&
      left.achievedAt === right.achievedAt
    );
  }
  private send(message: ClientMessage): void {
    if (this.socket?.readyState !== this.websocketOpenState) return;
    try {
      this.socket.send(encodeRealtimeMessage(message));
    } catch {
      this.socket.close();
    }
  }
  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.emit({ type: 'status', status });
  }
  private emit(event: RealtimeEvent): void {
    for (const listener of this.listeners) listener(event);
  }
}
